/* eslint-disable react/display-name */
import i18n from "i18n";
import moment from "moment";
import { Menu, Dropdown } from "antd";
import { EditOutlined, DeleteOutlined, EyeOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setPopup } from "providers/GeneralProvider/slice";
import { deleteContestRequest, updateContestRequest } from "providers/ContestProvider/slice";
import Helper from "utils/helpers";

const Columns = {};

const stripHtml = (html) => {
  if (!html) return "";
  return html.replace(/&nbsp;/g, ' ').replace(/<[^>]*>?/gm, '');
};

const renderDataValue = (value) => {
  const style = value ? {} : { color: "#c0c0c0" };
  return <span style={style}>{value || i18n.t("general.none")}</span>;
};

// Contest Menu Overlay Component
const ContestMenuOverlay = ({ record, refetchList }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleDeleteContest = () => {
    dispatch(
      setPopup({
        isOpen: true,
        data: {
          okText: "Xác nhận",
          cancelText: "Hủy",
          title: "Xác nhận xóa cuộc thi",
          content: `Bạn có muốn xóa cuộc thi "${record.name}"?`,
          onOk: () => {
            dispatch(deleteContestRequest(record._id))
              .then(() => {
                Helper.toastr(
                  "Xóa cuộc thi thành công",
                  "Cuộc thi đã được xóa thành công",
                  "success"
                );
                refetchList();
              })
              .catch((error) => {
                console.error("Delete contest error:", error);
                Helper.toastr(
                  "Xóa cuộc thi thất bại",
                  "Có lỗi xảy ra khi xóa cuộc thi. Vui lòng thử lại.",
                  "error"
                );
              });
          },
        },
      })
    );
  };

  const handleDisableContest = () => {
    dispatch(
      setPopup({
        isOpen: true,
        data: {
          okText: "Xác nhận",
          cancelText: "Hủy",
          title: "Xác nhận vô hiệu hóa cuộc thi",
          content: `Bạn có muốn vô hiệu hóa cuộc thi "${record.name}"?`,
          onOk: () => {
            dispatch(
              updateContestRequest({
                contestId: record._id,
                contestData: {
                  status: "disabled",
                },
              })
            )
              .then(() => {
                Helper.toastr(
                  "Vô hiệu hóa cuộc thi thành công",
                  "Cuộc thi đã được vô hiệu hóa thành công",
                  "success"
                );
                refetchList();
              })
              .catch((error) => {
                console.error("Disable contest error:", error);
                Helper.toastr(
                  "Vô hiệu hóa cuộc thi thất bại",
                  "Có lỗi xảy ra khi vô hiệu hóa cuộc thi. Vui lòng thử lại.",
                  "error"
                );
              });
          },
        },
      })
    );
  };

  const handleActivateContest = () => {
    dispatch(
      setPopup({
        isOpen: true,
        data: {
          okText: "Xác nhận",
          cancelText: "Hủy",
          title: "Xác nhận kích hoạt cuộc thi",
          content: `Bạn có muốn kích hoạt cuộc thi "${record.name}"?`,
          onOk: () => {
            dispatch(
              updateContestRequest({
                contestId: record._id,
                contestData: {
                  status: "active",
                },
              })
            )
              .then(() => {
                Helper.toastr(
                  "Kích hoạt cuộc thi thành công",
                  "Cuộc thi đã được kích hoạt thành công",
                  "success"
                );
                refetchList();
              })
              .catch((error) => {
                console.error("Activate contest error:", error);
                Helper.toastr(
                  "Kích hoạt cuộc thi thất bại",
                  "Có lỗi xảy ra khi kích hoạt cuộc thi. Vui lòng thử lại.",
                  "error"
                );
              });
          },
        },
      })
    );
  };

  return (
    <Menu>
      <Menu.Item
        icon={<EyeOutlined />}
        onClick={() => navigate(`/contest/${record._id}`, { state: { canGoBack: true } })}
      >
        Xem chi tiết
      </Menu.Item>
      <Menu.Item
        icon={<EditOutlined />}
        onClick={() => navigate(`/contest/${record._id}/edit`, { state: { canGoBack: true } })}
      >
        Chỉnh sửa
      </Menu.Item>
      
      {/* Activate Contest - Only show if contest is disabled or draft */}
      {(record.status === "disabled" || record.status === "draft") && (
        <Menu.Item
          icon={<EyeOutlined />}
          onClick={handleActivateContest}
        >
          Kích hoạt
        </Menu.Item>
      )}

      {/* Disable Contest - Only show if contest is active */}
      {record.status === "active" && (
        <Menu.Item
          icon={<EyeOutlined />}
          onClick={handleDisableContest}
        >
          Vô hiệu hóa
        </Menu.Item>
      )}

      <Menu.Item
        icon={<DeleteOutlined />}
        danger
        onClick={handleDeleteContest}
      >
        Xóa cuộc thi
      </Menu.Item>
    </Menu>
  );
};

Columns.id = {
  dataIndex: "_id",
  title: "#",
  render: (id, data) => renderDataValue(<div>#{id}</div>),
  width: 120,
};

Columns.name = {
  dataIndex: "name",
  title: "Tên cuộc thi",
  sorter: true,
  render: (name, data) => renderDataValue(<div>{name}</div>),
  width: 300,
};
Columns.accountName = {
  dataIndex: "name",
  title: "Tên KH",
  render: (name) => renderDataValue(<div>{name}</div>),
};

Columns.startDate = {
  title: "Ngày bắt đầu",
  dataIndex: "startDate",
  render: (startDate, data) =>
    renderDataValue(<div>{moment(startDate).format("DD/MM/YYYY")}</div>),
};
Columns.endDate = {
  title: "Ngày kết thúc",
  dataIndex: "endDate",
  render: (endDate, data) =>
    renderDataValue(<div>{moment(endDate).format("DD/MM/YYYY")}</div>),
};

const getStatusInfo = (status) => {
  switch (status) {
    case "draft":
      return {
        label: "📝 Nháp",
        className: "status-draft",
        color: "#8c8c8c",
      };
    case "active":
      return {
        label: "✅ Đang hoạt động",
        className: "status-active",
        color: "#52c41a",
      };
    case "disabled":
      return {
        label: "❌ Bị vô hiệu hóa",
        className: "status-disabled",
        color: "#ff4d4f",
      };
    case "ended":
      return {
        label: "🏁 Đã kết thúc",
        className: "status-ended",
        color: "#faad14",
      };
    default:
      return {
        label: status || "Không xác định",
        className: "status-unknown",
        color: "#d9d9d9",
      };
  }
};

Columns.status = {
  title: "Trạng thái",
  dataIndex: "status",
  render: (status, data) => {
    const statusInfo = getStatusInfo(status);
    return renderDataValue(
      <div
        className={statusInfo.className}
        style={{
          color: statusInfo.color,
          fontWeight: "500",
          padding: "4px 8px",
          borderRadius: "4px",
          backgroundColor: `${statusInfo.color}10`,
          border: `1px solid ${statusInfo.color}30`,
          display: "inline-block",
          fontSize: "12px",
        }}
      >
        {statusInfo.label}
      </div>
    );
  },
};
Columns.description = {
  title: "Mô tả",
  dataIndex: "description",
  render: (description, data) =>
    renderDataValue(
      <div style={{
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'normal',
        maxWidth: 300,
      }}>{stripHtml(description)}</div>
    ),
};

Columns.rules = {
  title: "Quy tắc",
  dataIndex: "rules",
  render: (rules, data) =>
    renderDataValue(
      <div style={{
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'normal',
        maxWidth: 300,
      }}>{stripHtml(rules)}</div>
    ),
};
Columns.totalCandidates = {
  title: "Tổng số ứng viên",
  dataIndex: "totalCandidates",
  render: (totalCandidates, data) =>
    renderDataValue(<div>{totalCandidates}</div>),
};
Columns.totalVotes = {
  title: "Tổng số bình chọn",
  dataIndex: "totalVotes",
  render: (totalVotes, data) => renderDataValue(<div>{totalVotes}</div>),
};

Columns.bannerImage = {
  title: "Banner",
  dataIndex: "bannerImage",
  key: "bannerImage",
  width: 330,
  render: (bannerImage) =>
    renderDataValue(
      <div className={`banner-image ${"vertical-banner"}`}>
        <img style={{ width: 200, height: 200 }} src={bannerImage} alt="" />
      </div>
    ),
};

// New optional fields
Columns.routeBanner = {
  title: "Banner lộ trình",
  dataIndex: "routeBanner",
  width: 200,
  render: (routeBanner, data) => 
    renderDataValue(
      routeBanner ? (
        <div className="banner-image">
          <img style={{ width: 150, height: 100, objectFit: "cover" }} src={routeBanner} alt="Route Banner" />
        </div>
      ) : null
    ),
};

Columns.logoContest = {
  title: "Logo cuộc thi",
  dataIndex: "logoContest",
  width: 150,
  render: (logoContest, data) => 
    renderDataValue(
      logoContest ? (
        <div className="logo-image">
          <img style={{ width: 80, height: 80, objectFit: "contain" }} src={logoContest} alt="Contest Logo" />
        </div>
      ) : null
    ),
};

Columns.timelineContest = {
  title: "Timeline cuộc thi",
  dataIndex: "timelineContest",
  width: 200,
  render: (timelineContest, data) => 
    renderDataValue(
      timelineContest ? (
        <div className="timeline-image">
          <img style={{ width: 150, height: 100, objectFit: "cover" }} src={timelineContest} alt="Timeline" />
        </div>
      ) : null
    ),
};

Columns.registerFormLink = {
  title: "Link form đăng ký",
  dataIndex: "registerFormLink",
  render: (registerFormLink, data) => 
    renderDataValue(
      registerFormLink ? (
        <a href={registerFormLink} target="_blank" rel="noopener noreferrer">
          Mở form
        </a>
      ) : null
    ),
};

// Add the actions column
Columns.actions = ({ onRefresh }) => ({
  title: "Thao tác",
  key: "actions",
  width: 80,
  fixed: "right",
  render: (_, record) => (
    <Dropdown
      overlay={<ContestMenuOverlay record={record} refetchList={onRefresh} />}
      trigger={["click"]}
    >
      <a onClick={(e) => e.preventDefault()}>Menu</a>
    </Dropdown>
  ),
});

export default Columns;
